<template>
  <div class="login-container">
    <!-- 背景装饰元素（纯CSS实现） -->
    <div class="bg-decor decor-1"></div>
    <div class="bg-decor decor-2"></div>
    <div class="bg-decor decor-3"></div>

    <!-- 登录卡片 -->
    <div class="login-card">
      <!-- 卡片头部 -->
      <div class="login-header">
        <div class="logo-icon">
          <img src="../assets/user.png" alt="">
          <!-- <i class="el-icon-satellite"></i> -->
        </div>
        <h1>物联网管理后台</h1>
        <p class="sub-title">高效管理你的物联网设备</p>
      </div>

      <!-- 登录表单 -->
      <el-form
        ref="loginForm"
        :model="loginForm"
        :rules="loginRules"
        class="login-form"
        label-position="left"
      >
        <el-form-item prop="username">
          <el-input
            v-model="loginForm.username"
            placeholder="请输入用户名"
            prefix-icon="el-icon-user"
            auto-complete="off"
            class="input-field"
          ></el-input>
        </el-form-item>

        <el-form-item prop="password">
          <el-input
            v-model="loginForm.password"
            type="password"
            placeholder="请输入密码"
            prefix-icon="el-icon-lock"
            auto-complete="off"
            @keyup.enter.native="handleLogin"
            class="input-field"
          ></el-input>
        </el-form-item>

        <!-- 记住密码选项 -->
        <el-form-item class="remember-item">
          <el-checkbox v-model="loginForm.remember" class="remember-checkbox">
            记住用户名
          </el-checkbox>
        </el-form-item>

        <el-form-item>
          <el-button
            type="primary"
            class="login-btn"
            @click="handleLogin"
            :loading="loading"
          >
            登录管理后台
          </el-button>
        </el-form-item>
      </el-form>

      <!-- 卡片底部提示 -->
      <div class="login-footer">
        <p>© 2024 物联网管理系统 | 安全加密传输</p>
      </div>
    </div>
  </div>
</template>

<script>
import { login, getPublicKey } from '@/api/login'
import JSEncrypt from 'jsencrypt'

export default {
  name: 'Login',
  data() {
    return {
      loginForm: {
        username: '',
        password: '',
        remember: false
      },
      loginRules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 3, max: 20, message: '用户名长度在 3 到 20 个字符', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, max: 32, message: '密码长度在 6 到 32 个字符', trigger: 'blur' }
        ]
      },
      loading: false,
      publicKey: ''
    }
  },
  created() {
    this.getRsaPublicKey()
    this.getRememberedPassword()
  },
  methods: {
    async getRsaPublicKey() {
      try {
        const res = await getPublicKey()
        if (res.publicKey) {
          this.publicKey = `-----BEGIN PUBLIC KEY-----\n${res.publicKey}\n-----END PUBLIC KEY-----`
        } else {
          this.$message.error('获取加密公钥失败，无法登录')
        }
      } catch (error) {
        console.error('获取公钥异常：', error)
        this.$message.error('网络异常，无法获取加密信息')
      }
    },

    getRememberedPassword() {
      const rememberedUser = localStorage.getItem('rememberedUser')
      if (rememberedUser) {
        const user = JSON.parse(rememberedUser)
        this.loginForm = {
          username: user.username,
          password: user.password,
          remember: true
        }
      }
    },

    rsaEncrypt(text) {
      if (!this.publicKey) {
        this.$message.error('加密公钥未就绪，请刷新页面重试')
        return null
      }
      const encryptor = new JSEncrypt()
      encryptor.setPublicKey(this.publicKey)
      const encryptedText = encryptor.encrypt(text)
      if (!encryptedText) {
        this.$message.error('密码加密失败，请检查公钥格式')
        return null
      }
      return encryptedText
    },

    handleLogin() {
      const that = this
      this.$refs.loginForm.validate(async valid => {
        if (valid) {
          if (!that.publicKey) {
            that.$message.error('加密公钥未就绪，请稍候重试')
            return
          }

          that.loading = true
          try {
            const encryptedPassword = that.rsaEncrypt(that.loginForm.password)
            if (!encryptedPassword) return

            const loginParams = {
              account: that.loginForm.username,
              password: encryptedPassword
            }

            const res = await login(loginParams)
            if (res.code === 0) {
              localStorage.setItem('token', res.data.token)
              localStorage.setItem('user', JSON.stringify(res.data))

              // 处理记住密码
              if (that.loginForm.remember) {
                localStorage.setItem('rememberedUser', JSON.stringify({
                  username: that.loginForm.username,
                  password: that.loginForm.password
                }))
              } else {
                localStorage.removeItem('rememberedUser')
              }

              that.$router.push('/dashboard')
              that.$message.success('登录成功，正在跳转...')
            } else {
              that.$message.error(res.msg || '登录失败')
            }
          } catch (error) {
            console.error('登录请求异常：', error)
            that.$message.error('网络错误，请稍后重试')
          } finally {
            that.loading = false
          }
        }
      })
    }
  }
}
</script>

<style scoped>
/* 1. 全局背景样式：渐变色+装饰元素 */
.login-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  /* 柔和蓝紫渐变背景 */
  background: linear-gradient(135deg, #e0f7fa 0%, #e3f2fd 50%, #ede7f6 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

/* 背景装饰圆（纯CSS，无图片） */
.bg-decor {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  animation: float 15s infinite ease-in-out;
}

.decor-1 {
  width: 600px;
  height: 600px;
  top: -200px;
  left: -200px;
  animation-delay: 0s;
}

.decor-2 {
  width: 400px;
  height: 400px;
  bottom: -150px;
  right: -150px;
  animation-delay: 5s;
}

.decor-3 {
  width: 300px;
  height: 300px;
  top: 50%;
  right: 10%;
  transform: translateY(-50%);
  animation-delay: 2s;
}

/* 装饰圆浮动动画 */
@keyframes float {
  0% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(30px, 30px) scale(1.05);
  }
  100% {
    transform: translate(0, 0) scale(1);
  }
}

/* 2. 登录卡片样式：立体悬浮感 */
.login-card {
  width: 420px;
  padding: 40px 35px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(31, 38, 135, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  /* 卡片入场动画 */
  opacity: 0;
  transform: translateY(20px);
  animation: cardFadeIn 0.8s forwards ease-out;
}

/* 卡片入场动画 */
@keyframes cardFadeIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 3. 卡片头部样式 */
.login-header {
  text-align: center;
  margin-bottom: 35px;
}

.logo-icon img {
  width: 60px;
  height: 60px;
  margin: 0 auto 15px;
  background: linear-gradient(135deg, #1890ff 0%, #646cff 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 24px;
  box-shadow: 0 4px 15px rgba(24, 144, 255, 0.3);
}

.login-header h1 {
  color: #1f2937;
  font-size: 26px;
  margin-bottom: 8px;
  font-weight: 600;
}

.sub-title {
  color: #6b7280;
  font-size: 14px;
  opacity: 0.8;
}

/* 4. 表单元素样式优化 */
.login-form {
  margin-bottom: 25px;
}

/* 输入框样式 */
.input-field {
  height: 50px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  font-size: 15px;
  transition: all 0.3s ease;
}

.input-field:focus {
  border-color: #1890ff;
  box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1);
}

/* 记住密码选项 */
.remember-item {
  margin-bottom: 28px;
  padding-left: 2px;
}

.remember-checkbox {
  color: #6b7280;
  font-size: 14px;
  cursor: pointer;
}

.remember-checkbox:hover {
  color: #1890ff;
}

/* 5. 登录按钮样式 */
.login-btn {
  width: 100%;
  height: 52px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 500;
  background: linear-gradient(135deg, #1890ff 0%, #646cff 100%);
  border: none;
  transition: all 0.3s ease;
}

.login-btn:hover {
  background: linear-gradient(135deg, #0f7ae5 0%, #535bf2 100%);
  box-shadow: 0 6px 15px rgba(24, 144, 255, 0.25);
  transform: translateY(-2px);
}

.login-btn:active {
  transform: translateY(0);
  box-shadow: 0 4px 10px rgba(24, 144, 255, 0.2);
}

/* 6. 卡片底部样式 */
.login-footer {
  text-align: center;
  margin-top: 15px;
}

.login-footer p {
  color: #9ca3af;
  font-size: 12px;
  opacity: 0.9;
}

/* 7. 响应式适配：小屏幕调整 */
@media (max-width: 576px) {
  .login-card {
    width: 90%;
    padding: 35px 25px;
  }

  .decor-3 {
    display: none; /* 小屏幕隐藏第三个装饰圆 */
  }
}
</style>
